import React from 'react';
import { liveQuery } from "dexie";
import { db } from '../db';
import { request } from '../functions/apiRequestWrapper';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';
import { CheckCircleFill } from 'react-bootstrap-icons';

import { connect } from 'react-redux';
const mapStateToProps = state => {
    return {
        releases: state.releases.list,
        isLoadingReleases: state.releases.isLoading
    };
}

//https://github.com/dfahlander/Dexie.js/issues/1231#issuecomment-779723851
const observable = liveQuery (
    () => db.releases.orderBy("yyyymm").reverse().toArray()
);

class Download extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            dbReleases: [],
            selectedRelease: null,
            downloadStarted: 0,
            downloadDone: 0,
            downloadFailedFetch: 0,
            downloadFailedDb: 0,
            downloadCovers: [],
            downloadCachingDone: false,
            error: false
        }
    }

    componentDidMount() {
        this.subscription = observable.subscribe(
            result => this.setState({dbReleases: result}),
            error => this.setState({error, dbReleases: []})
        );
    }

    componentWillUnmount() {
        if (this.subscription) {
            this.subscription.unsubscribe();
            this.subscription = null;
        }
    }

    downloadRelease = async () => {
        if (!this.state.selectedRelease) return;

        const CONCURRENCY = 5;
        const slug = this.state.selectedRelease;
        const months = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];

        let started = 1, done = 0, failedFetch = 0, failedDb = 0, covers = [];
        this.setState({downloadStarted: started, downloadDone: done, downloadFailedFetch: failedFetch, downloadFailedDb: failedDb});

        // 1. Fetch release
        let releaseData;
        try {
            releaseData = await request(`${process.env.REACT_APP_API}/1/releases/${slug}`);
        } catch (e) {
            this.setState({downloadFailedFetch: ++failedFetch});
            return;
        }

        const slugSplit = slug.split("-");
        const ym = slugSplit[1] + (months.indexOf(slugSplit[0]) + 1).toString().padStart(2, '0');
        const listRelease = this.props.releases.find(r => r.slug === slug);
        const imageUrls = listRelease?.imageUrls || [];
        const release = {slug: releaseData.slug, catalogue: releaseData.catalogue, imageUrls: imageUrls, coverImage: releaseData.coverImage, yyyymm: ym};
        db.releases.put(release)
            .then(() => this.setState({downloadDone: ++done}))
            .catch(() => this.setState({downloadFailedDb: ++failedDb}));

        // 2. Fetch all section product lists in parallel (few sections, this is fine)
        const sections = releaseData.sections || [];
        started += sections.length;
        this.setState({downloadStarted: started});

        const sectionResults = await Promise.all(sections.map(async (section, s) => {
            section.idx = s;
            section.products = []; // we only want to store a list of isbns on the section, not full products
            delete section.orderForm; // we don't need download links when offline
            delete section.catalogueOnixFull;
            delete section.catalogueOnixBasic;
            try {
                const sectionData = await request(`${process.env.REACT_APP_API}/1/releases/${release.slug}/${section.orderFormId}/products`);
                section.products = (sectionData.products || []).map(p => String(p.isbn));
                return {section, products: sectionData.products || [], loaded: true};
            } catch (e) {
                this.setState({downloadFailedFetch: ++failedFetch});
                return {section, products: [], loaded: false};
            }
        }));

        // 3. Build flat product queue and update total count
        const productQueue = sectionResults.flatMap(({section, products}) =>
            products.map(product => ({product, section}))
        );
        started += productQueue.length;
        this.setState({downloadStarted: started});

        // 4. Process products with a concurrency-limited worker pool
        const isbnToImageUrl = {};
        const cachePromises = [];
        const queue = [...productQueue];
        const cacheImage = (cache, url) => {
            return fetch(url, {mode: 'no-cors'})
                .then(r => cache.put(url, r))
                .catch(() => {});
        };
        if ('caches' in window && imageUrls.length > 0) {
            cachePromises.push(
                caches.open('release-covers').then(cache =>
                    Promise.all(imageUrls.map(url => cacheImage(cache, url.replace('x1000.', 'x180.'))))
                )
            );
        }
        const offlineImagesCache = ('caches' in window) ? await caches.open('offline-images') : null;

        const runWorker = async () => {
            while (queue.length > 0) {
                const {product} = queue.shift();

                let bookData;
                try {
                    bookData = await request(`${process.env.REACT_APP_API}/1/product/${product.isbn}`);
                } catch (e) {
                    this.setState({downloadFailedFetch: ++failedFetch});
                    continue;
                }

                const [internalImages, marketingImages] = await Promise.all([
                    bookData.hasInternalImage
                        ? request(`${process.env.REACT_APP_API}/1/service/isbn/${product.isbn}?folderName=internal_images`).catch(() => [])
                        : Promise.resolve([]),
                    bookData.hasMarketingMaterials
                        ? request(`${process.env.REACT_APP_API}/1/service/isbn/${product.isbn}?folderName=marketing`).catch(() => [])
                        : Promise.resolve([])
                ]);
                bookData.internalImages = Array.isArray(internalImages) ? internalImages : [];
                bookData.marketingImages = Array.isArray(marketingImages) ? marketingImages : [];

                if (offlineImagesCache) {
                    const x180url = bookData.imageUrl && bookData.imageUrl.includes('x1000.')
                        ? bookData.imageUrl.replace('x1000.', 'x180.')
                        : null;
                    await Promise.all(
                        [...bookData.internalImages, ...bookData.marketingImages, bookData.imageUrl, x180url]
                            .filter(Boolean)
                            .map(url => cacheImage(offlineImagesCache, url))
                    );
                }

                try {
                    await db.products.put(bookData);
                    if (bookData.imageUrl) isbnToImageUrl[String(bookData.isbn)] = bookData.imageUrl;
                    if (bookData.imageUrl) covers.push(bookData.imageUrl);
                    this.setState({downloadDone: ++done, downloadCovers: [...covers]});
                } catch (e) {
                    this.setState({downloadFailedDb: ++failedDb});
                }
            }
        };
        await Promise.all(Array.from({length: Math.min(CONCURRENCY, productQueue.length)}, runWorker));

        // Overwrite section imageUrls with the product imageUrls cached in offline-images during download,
        // so section book stacks load from cache when offline.
        for (const {section} of sectionResults) {
            const sectionImageUrls = section.products
                .map(isbn => isbnToImageUrl[isbn])
                .filter(Boolean)
                .slice(0, 4);
            if (sectionImageUrls.length > 0) {
                section.imageUrls = sectionImageUrls;
            }
        }

        // 5. Save sections now that all product ISBNs are collected
        for (const {section, loaded} of sectionResults) {
            if (!loaded) continue;
            try {
                await db.sections.put(section);
                this.setState({downloadDone: ++done});
            } catch (e) {
                this.setState({downloadFailedDb: ++failedDb});
            }
        }

        await Promise.all(cachePromises);
        this.setState({downloadCachingDone: true});
    }

    reset = () => {
        this.setState({
            downloadStarted: 0,
            downloadDone: 0,
            downloadFailedFetch: 0,
            downloadFailedDb: 0,
            downloadCovers: [],
            downloadCachingDone: false,
        });
    }

    deleteAll = () => {
        if ('caches' in window) {
            caches.delete('release-covers');
            caches.delete('offline-images');
        }
        db.delete().then(() => db.open().then(() => {
            this.subscription = observable.subscribe(
                result => this.setState({dbReleases: result}),
                error => this.setState({error, dbReleases: []})
            );
        }));
    }

    render() {
        const {downloadStarted, downloadDone, downloadFailedDb, downloadFailedFetch, dbReleases, downloadCovers, downloadCachingDone} = this.state;
        return (
            <div>
                <p className="mb-3">Use this form to download Release data for offline use.</p>
                <Form.Group className="form-group">
                    <Form.Label>Select release</Form.Label>
                    <Form.Control
                        as="select"
                        value={this.state.selectedRelease || ""}
                        onChange={(e) => {
                            this.setState({selectedRelease: e.target.value})
                        }}
                    >
                        <option value=""></option>
                        {this.props.releases.map((r, i) => {
                            return (<option value={r.slug} key={i}>{r.catalogue}</option>);
                        })}
                    </Form.Control>
                </Form.Group>

                {downloadStarted < 1 && <p><Button onClick={this.downloadRelease}>Start</Button></p>}

                {downloadStarted > 0 && <div>
                    <p className="d-flex align-items-center">
                        {downloadCachingDone
                            ? <CheckCircleFill size={16} className="me-1 text-success" />
                            : <Spinner animation="border" size="sm" className="me-1" />
                        }
                        {downloadCachingDone ? 'Completed:' : 'Downloading:'} {downloadDone + downloadFailedDb + downloadFailedFetch} / {downloadStarted}
                    </p>
                    {(downloadFailedDb > 0 || downloadFailedFetch > 0) && <p className="text-success">Success: {downloadDone} / {downloadStarted}</p>}
                    {downloadFailedFetch > 0 && <p className="text-danger">Failed to fetch: {downloadFailedFetch} / {downloadStarted}</p>}
                    {downloadFailedDb > 0 && <p className="text-danger">Failed to save: {downloadFailedDb} / {downloadStarted}</p>}
                    <p className="download-covers d-flex flex-wrap">
                        {downloadCovers && downloadCovers.length > 0 &&
                            downloadCovers.map((img, i) => <img src={img} alt="" />)
                        }
                    </p>
                </div>}

                {downloadStarted > 0 && (downloadDone + downloadFailedDb + downloadFailedFetch) === downloadStarted && downloadCachingDone && <p><Button onClick={this.reset}>Download another release</Button></p>}

                <hr />

                <h5>Saved releases</h5>
                {(!dbReleases || dbReleases.length === 0) && <p>No releases saved</p>}
                {dbReleases && dbReleases.length >= 3 && <Alert variant="warning">
                    <p>Please note, the amount of space available for offline storage is limited by your browser, your device, and the amount of free space available.</p>
                    <p>Although it is theoretically possible to store as many releases as you want, we recommend 3 or less.</p>
                </Alert>}
                {dbReleases && dbReleases.map((r, i) => {
                    return (
                        <p key={r.slug}>{r.catalogue}</p>
                    );
                })}
                {dbReleases && dbReleases.length > 0 && <p><Button onClick={this.deleteAll}>Delete all</Button></p>}
            </div>
        );
    }
}

export default connect(mapStateToProps)(Download);
